import { Box } from '@mui/material';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium';
import React, { useCallback, useMemo, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import LexicaljsEditor from '../../lexical/LexicaljsEditor';
import type { LexicalSlashMacroAction } from '../../lexical/LexicalSlashMacroPlugin';

type RichTextEditCellProps = GridRenderEditCellParams & {
    slashMacroActions?: ReadonlyArray<LexicalSlashMacroAction>;
    autoOpenSlashMacro?: boolean;
    enableSlashMacroAutoOpenOnRootClick?: boolean;
    suppressAutoOpenSlashMacro?: boolean;
};

/**
 * Inline grid editor for rich text (HTML) content.
 *
 * Renders the Lexical editor directly inside the cell, with the formatting
 * toolbar hidden so it behaves like a regular text box; formatting stays
 * available through keyboard shortcuts (e.g. Cmd/Ctrl+B for bold) and the
 * existing Lexical plugins (@ mentions, links, lists) keep working.
 *
 * Edits flow into the standard grid commit path via `setEditCellValue`.
 * The row remains in edit mode until the user explicitly saves or cancels from
 * the grid row actions.
 */
export default function RichTextEditCell({
    id,
    field,
    value,
    row,
    hasFocus,
    slashMacroActions,
    autoOpenSlashMacro,
    enableSlashMacroAutoOpenOnRootClick,
    suppressAutoOpenSlashMacro
}: RichTextEditCellProps) {
    const apiRef = useGridApiContext();

    const initialValueRef = useRef<string>(typeof value === 'string' ? value : '');

    const handleChange = useCallback(
        (html: string) => {
            apiRef.current.setEditCellValue({ id, field, value: html });
        },
        [apiRef, field, id]
    );

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        // Enter creates a new line inside the editor; don't let the grid commit the row.
        if (event.key === 'Enter') {
            event.stopPropagation();
        }
    }, []);

    const slashMacroActionsWithGridContext = useMemo(
        () =>
            slashMacroActions?.map((action) => ({
                ...action,
                onSelect: (selection) =>
                    action.onSelect?.({
                        ...selection,
                        context: apiRef.current.getRowWithUpdatedValues(id, field) ?? row
                    })
            })),
        [apiRef, field, id, row, slashMacroActions]
    );

    return (
        <Box onKeyDown={handleKeyDown} sx={{ width: '100%', alignSelf: 'stretch', py: 0.5 }}>
            <LexicaljsEditor
                id={`rich-text-edit-${field}`}
                content={initialValueRef.current}
                contentPlaceholder=""
                onChange={handleChange}
                shouldAutoFocus={hasFocus}
                isSmall
                isEditing
                hideToolbar
                disableTranslation
                slashMacroActions={slashMacroActionsWithGridContext}
                autoOpenSlashMacro={autoOpenSlashMacro && hasFocus}
                enableSlashMacroAutoOpenOnRootClick={enableSlashMacroAutoOpenOnRootClick}
                suppressAutoOpenSlashMacro={suppressAutoOpenSlashMacro}
                sx={{
                    minHeight: 'auto',
                    border: 'none'
                }}
            />
        </Box>
    );
}
